import { Icon } from '@iconify/react'
import { observable, observe } from '@legendapp/state'
import { useObservable } from '@legendapp/state/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import { fetchDistributorList, updateDistributorStatus } from '~features/distributors/api'
import { DistributorListQuery, Distributors } from '~features/distributors/types'
import { Button, Card, CardContent, DropdownMenuComponent } from '~in-app/components'
import { FilterDropdownProps, TableContextProvider, TableState$ } from '~in-app/components/table/TableContext'
import { auth$ } from '~in-app/legend-state'
import { DistributorPersonsInCharge } from '~in-app/shareable/index.types'

export const Route = createFileRoute('/console/distributor/list')({
  component: DistributorPage,
  staticData: {
    privileges: ['0.3.7.0'],
  },
})
const columnHelper = createColumnHelper<Distributors>()
const defaultState$ = observable<{
  sortList: { id: string, desc: boolean }[]
  pagination: number
  pageSize: number
  queries: string
  status: string[]
}>({
  sortList: [
    { id: 'name', desc: true },
    { id: 'status', desc: true },
  ],
  pagination: 0,
  pageSize: 20,
  queries: new URLSearchParams(window.location.search).get('queries') || '',
  status: (new URLSearchParams(window.location.search).get('status') || 'ACTIVE,EXPIRED,INACTIVE').split(','),
})

function DistributorPage() {
  const navigate = Route.useNavigate()

  const dropdownItems = (value: Distributors) => {
    return [
      {
        label: 'Edit',
        onClick: () => navigate({ to: `/console/distributor/edit/${value.id}` }),
        disabled: !auth$.privileges.get()?.has('0.3.7.2'),
        icon: (
          <Icon
            height="16"
            icon="lucide:edit-2"
            width="16"
          />
        ),
      },
      {
        label: value.status === 'ACTIVE' ? 'Deactivate' : 'Activate',
        onClick: () => handleUpdateDistributorStatus(value.id, value.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'),
        disabled: !auth$.privileges.get()?.has('0.3.7.3'),
        icon: value.status === 'ACTIVE'
          ? (
              <Icon
                height="16"
                icon="lucide:circle-x"
                width="16"
              />
            )
          : (
              <Icon
                height="16"
                icon="lucide:circle-check"
                width="16"
              />
            ),
      },
    ]
  }
  const filterDropdownItems: FilterDropdownProps[] = [
    {
      label: 'Search', // New dropdown for controlling sidebar
      type: 'text',
      value: new URLSearchParams(window.location.search).get('queries') || '',
      result: new URLSearchParams(window.location.search).get('queries') || '',
    },
    {
      label: 'Status',
      type: 'dropdown',
      isMultiSelect: true,
      options: [
        {
          label: 'Active',
          value: 'ACTIVE',
        },
        {
          label: 'Expired',
          value: 'EXPIRED',
        },
        {
          label: 'Inactive',
          value: 'INACTIVE',
        },
      ],
      value: new URLSearchParams(window.location.search).get('status')?.split(',').map(status => ({
        label: status.charAt(0).toUpperCase() + status.slice(1),
        value: status,
      })) || [{
        label: 'Active',
        value: 'ACTIVE',
      },
      {
        label: 'Expired',
        value: 'EXPIRED',
      },
      {
        label: 'Inactive',
        value: 'INACTIVE',
      },
      ],
      result: new URLSearchParams(window.location.search).get('status')?.split(',').map(status => ({
        label: status.charAt(0).toUpperCase() + status.slice(1),
        value: status,
      })) || [{
        label: 'Active',
        value: 'ACTIVE',
      },
      {
        label: 'Expired',
        value: 'EXPIRED',
      },
      {
        label: 'Inactive',
        value: 'INACTIVE',
      },
      ],
    },
  ]
  const columns = [
    columnHelper.accessor('id', {
      cell: info => info.getValue(),
      header: () => <span>ID</span>,
    }),
    columnHelper.accessor('name', {
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('status', {
      cell: info => info.getValue(),
      header: () => <span>Status</span>,
      enableSorting: true,
      size: 110,
    }),
    columnHelper.accessor('address.line1', {
      cell: info => info.getValue(),
      header: () => <span>Address 1</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('address.line2', {
      cell: info => info.getValue(),
      header: () => <span>Address 2</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('address.line3', {
      cell: info => info.getValue() || '-',
      header: () => <span>Address 3</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('address.postcode', {
      cell: info => info.getValue(),
      header: () => <span>Postcode</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('address.city', {
      cell: info => info.getValue(),
      header: () => <span>City</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('address.state', {
      cell: info => info.getValue(),
      header: () => <span>State</span>,
      enableSorting: true,
      size: 150,
    }),
    columnHelper.accessor('code', {
      cell: info => info.getValue(),
      header: () => <span>Code</span>,
      enableSorting: true,
      size: 150,
    }),
    columnHelper.accessor('desp', {
      cell: info => info.getValue(),
      header: () => <span>Description</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('personsInCharge', {
      cell: (info) => {
        const persons = info.getValue<DistributorPersonsInCharge[]>() // Get the array of personsInCharge
        return persons === undefined
          ? '-'
          : persons?.map((person: { firstName: string, lastName: string }) => `${person.firstName} ${person.lastName}`)
            .join(', ')
      },
      header: () => <span>Persons In Charge</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('remarks', {
      cell: info => info.getValue(),
      header: () => <span>Remarks</span>,
      enableSorting: true,
    }),

    columnHelper.accessor('tempPwd', {
      cell: info => info.getValue(),
      header: () => <span>Temp Password</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('timezone', {
      cell: info => info.getValue(),
      header: () => <span>Timezone</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('action', {
      cell: info => (
        <span>
          <DropdownMenuComponent
            preventClose
            className="rounded-md bg-transparent p-2 hover:opacity-100"
            items={dropdownItems(info.row.original)}
            buttonIcon={
              (
                <Icon icon="lucide:ellipsis-vertical" />
              )
            }
          />

        </span>
      ),
      header: () => <span />,
      enableSorting: false,
      size: 50,
    }),
  ]

  const state$ = useObservable(defaultState$)
  const tableState$ = useObservable<TableState$<Distributors[]>>(() => ({
    id: 'distributor-table',
    isSortable: true,
    data: [],
    columns: columns,
    hideColumns: ['id'],
    sort: state$.sortList.get(),
    totalItem: 0,
    totalPage: 0,
    pageSize: state$.pageSize.get(),
    pagination: state$.pagination.get(),
    pageIndex: 0,
    filters: filterDropdownItems,
    onApply: (value: any) => {
      const searchParams: Record<string, string> = {}

      const textFilter = value.find((item: any) => item.type === 'text')
      const textValue = textFilter?.result?.trim() || ''
      state$.queries.set(textValue)
      searchParams.queries = textValue

      const status = value.find((item: any) => item.label === 'Status')

      // Construct query object

      if (status?.result) {
        state$.status.set(status.result.map((item: any) => item.value))
        searchParams.status = status.result.map((item: any) => item.value).join(',')
      }
      else {
        searchParams.status = 'ACTIVE,EXPIRED,INACTIVE'
      }

      // Navigate with updated params
      navigate({
        search: (prev) => {
          const updatedSearch: Record<string, any> = { ...prev, ...searchParams } // Explicitly type as Record<string, any>

          // Remove empty or undefined keys safely
          if (!textValue) delete updatedSearch.queries

          return updatedSearch
        },
      })
    },
    setSort: (value: any) => {
      state$.sortList.set(value)
    },
    setPageSize: (value: any) => {
      state$.pageSize.set(value)
      navigate({ search: prev => ({ ...prev, pageSize: value }) })
    },
    setPagination: (value: any) => {
      state$.pagination.set(value)
      navigate({ search: prev => ({ ...prev, page: value + 1 }) })
    },
  }))

  // // useEffect to call fetchDistributorList when pageSize, pagination, or sortList changes
  // state$.pageSize.onChange(() => {
  //   fetchDistributorList()
  // })
  // state$.pagination.onChange(() => {
  //   fetchDistributorList()
  // })
  // state$.sortList.onChange(() => {
  //   fetchDistributorList()
  // })
  // useEffect(() => {
  //   fetchDistributorList()
  // }, []) // Dependencies updated here
  useEffect(() => {
    state$.pagination.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('page')
        return match ? parseInt(match, 10) - 1 : 0
      })(),
    )
    state$.pageSize.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('pageSize')
        return match ? parseInt(match, 10) : 20
      })(),
    )
    const disposer = observe(state$, (e) => {
      if (e.previous !== undefined && e.value !== undefined) {
        refetch()
      }
    }, { immediate: false })

    return disposer
  }, [])
  const queryClient = useQueryClient()
  const updateDistributorStatusFunc = () => {
    const mutation = useMutation({
      mutationFn: ({ id, status }: { id: string, status: string }) => updateDistributorStatus(id, status),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['distributor-list'] })
        toast.success('Integrator deleted successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const updateDistributorStatusMutation = updateDistributorStatusFunc()
  const handleUpdateDistributorStatus = (id: string, status: string) => {
    updateDistributorStatusMutation.mutate({ id, status })
  }
  const { refetch } = useQuery({
    queryKey: ['distributor-list'],
    queryFn: async ({ signal }) => {
      const params = {
        page: state$.pagination.get() || 0,
        size: state$.pageSize.get() || 20,
        queries: state$.queries.get() || undefined,
        statuses: state$.status.get().join(',') || undefined,
        sort: state$.sortList
          .get()
          .map((sortByVal) => {
            return `${sortByVal.id},${sortByVal.desc ? 'desc' : 'asc'}`
          })
          .join(','),
      }

      const response = await fetchDistributorList({ abortCtrl: signal, query: params as DistributorListQuery })
      const newDistributorList = response?.list.map((item: any) => ({
        ...item,
        action: '',
      }))
      tableState$.data.set(newDistributorList || [])
      tableState$.totalItem.set(response?.totalItem || 0)
      tableState$.totalPage.set(response?.totalPage || 0)
      if ((response?.totalPage ?? 0) < (state$.pagination.get() + 1)) {
        state$.pagination.set(0)
        navigate({ search: prev => ({ ...prev, page: 1 }) })
      }
      return response
    },
  })

  return (
    <div>
      <div className="flex items-center justify-between p-2">
        <span className="mb-4 ml-4 text-2xl font-bold">Distributor Page</span>

        <Button
          className="rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
          color="muted"
          disabled={!auth$.privileges.get()?.has('0.3.7.1')}
          onClick={() => navigate({ to: '/console/distributor/add' })}
        >
          Add
        </Button>
      </div>

      <div className="p-2">
        <Card className="mt-4">
          <CardContent>
            <div>
              <TableContextProvider state$={tableState$} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>

  )
}
